import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Phone,
  MessageSquare,
  TrendingUp,
  Search,
  Eye,
  UserPlus,
  CheckCircle,
  Clock,
  FileText,
  Bot,
  Edit,
  Trash2,
  Send,
  ArrowRight,
  History,
  Mail,
  FileJson,
  AlertTriangle,
  Upload,
  ChevronDown,
  Plus
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LeadAssignmentModal from './modals/LeadAssignmentModal';
import AddLeadModal from './modals/AddLeadModal';
import BulkUploadModal from './modals/BulkUploadModal';
import QuotationForm from './QuotationForm';
import QuotationViewModal from './QuotationViewModal';
import quotationsAPI from '../services/quotationsAPI';
import { pbxCallAPI } from '../services/api';
import { adminRequirementsAPI } from '../services/api';
import '../styles/LeadManagement.css';
import { getDisplayPhoneNumber } from '../utils/numberMasking';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const formatActivityLabel = (activity) => {
  if (activity.activity_type === 'quotation_sent' && activity.activity_data?.sent_by_name) {
    return `Quotation sent by ${activity.activity_data.sent_by_name}`;
  }

  return activity.activity_type.replace(/_/g, ' ');
};

// Normalize bot_conversation_data (jsonb) into an array of {text, role}
const parseTranscript = (raw) => {
  if (!raw) return [];
  let data = raw;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch { return []; }
  }
  const turns = Array.isArray(data) ? data : (data.conversation || data.messages || []);
  if (!Array.isArray(turns)) return [];

  return turns.map((t, i) => ({
    text: t.text || t.message || t.content || '',
    role: (t.role || t.speaker || t.sender || t.from || t.type || (i % 2 === 0 ? 'bot' : 'user'))
      .toString().toLowerCase()
  })).filter(t => t.text);
};

// ── Country Codes Database ───────────────────────────────────────────────────
const COUNTRY_CODES = [
  { code: '+1', country: 'United States', flag: '🇺🇸', iso: 'US' },
  { code: '+1', country: 'Canada', flag: '🇨🇦', iso: 'CA' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧', iso: 'GB' },
  { code: '+91', country: 'India', flag: '🇮🇳', iso: 'IN' },
  { code: '+86', country: 'China', flag: '🇨🇳', iso: 'CN' },
  { code: '+81', country: 'Japan', flag: '🇯🇵', iso: 'JP' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷', iso: 'KR' },
  { code: '+33', country: 'France', flag: '🇫🇷', iso: 'FR' },
  { code: '+49', country: 'Germany', flag: '🇩🇪', iso: 'DE' },
  { code: '+39', country: 'Italy', flag: '🇮🇹', iso: 'IT' },
  { code: '+34', country: 'Spain', flag: '🇪🇸', iso: 'ES' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱', iso: 'NL' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭', iso: 'CH' },
  { code: '+43', country: 'Austria', flag: '🇦🇹', iso: 'AT' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪', iso: 'SE' },
  { code: '+47', country: 'Norway', flag: '🇳🇴', iso: 'NO' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰', iso: 'DK' },
  { code: '+358', country: 'Finland', flag: '🇫🇮', iso: 'FI' },
  { code: '+48', country: 'Poland', flag: '🇵🇱', iso: 'PL' },
  { code: '+420', country: 'Czech Republic', flag: '🇨🇿', iso: 'CZ' },
  { code: '+36', country: 'Hungary', flag: '🇭🇺', iso: 'HU' },
  { code: '+40', country: 'Romania', flag: '🇷🇴', iso: 'RO' },
  { code: '+30', country: 'Greece', flag: '🇬🇷', iso: 'GR' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷', iso: 'TR' },
  { code: '+380', country: 'Ukraine', flag: '🇺🇦', iso: 'UA' },
  { code: '+7', country: 'Russia', flag: '🇷🇺', iso: 'RU' },
  { code: '+972', country: 'Israel', flag: '🇮🇱', iso: 'IL' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', iso: 'SA' },
  { code: '+971', country: 'United Arab Emirates', flag: '🇦🇪', iso: 'AE' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', iso: 'MY' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', iso: 'SG' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭', iso: 'TH' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳', iso: 'VN' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩', iso: 'ID' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭', iso: 'PH' },
  { code: '+61', country: 'Australia', flag: '🇦🇺', iso: 'AU' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿', iso: 'NZ' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷', iso: 'BR' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷', iso: 'AR' },
  { code: '+56', country: 'Chile', flag: '🇨🇱', iso: 'CL' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴', iso: 'CO' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽', iso: 'MX' },
];

const AGENT_API_URL = process.env.REACT_APP_AGENT_API_URL ||
  API_BASE_URL.replace(/\/api$/, '') + '/agent-api';

const LeadManagement = ({
  user,
  embedded = false,
  onCreateQuotation,
  assignedOnly = false,
  rbacRoleId = null,
  viewMode = 'owner',      // legacy alias — use scope instead
  scope = null,            // 'self' | 'hierarchy' (canonical)
  readonly = false,        // hide edit/assign/delete actions
  roleName = null          // display name for the viewed role (used in banner)
}) => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [hierarchyMeta, setHierarchyMeta] = useState(null); // {roleCount, depth} for scope=hierarchy
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [activities, setActivities] = useState([]);
  const [roles, setRoles] = useState([]);

  // Interaction trail state
  const [newInteraction, setNewInteraction] = useState({
    type: 'comment',
    notes: ''
  });
  const [submittingInteraction, setSubmittingInteraction] = useState(false);

  // Quotation modal state
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [leadQuotations, setLeadQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  // Quotation actions state
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [sendingWhatsappId, setSendingWhatsappId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // WhatsApp Chat Starter State
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatPhoneEdited, setNewChatPhoneEdited] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES.find(c => c.iso === 'IN'));
  const [newChatSending, setNewChatSending] = useState(false);
  const [newChatToast, setNewChatToast] = useState(null);
  const [selectedTemplateForChat, setSelectedTemplateForChat] = useState('tecnvi_he');

  // Follow-up Scheduler State
  const [followups, setFollowups] = useState([]);
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [schedulingFollowup, setSchedulingFollowup] = useState(false);
  const [newFollowup, setNewFollowup] = useState({
    scheduled_date: '',
    scheduled_time: '',
    mode: 'whatsapp',
    template_name: ''
  });
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  // Debug Modal State
  const [showRawDataModal, setShowRawDataModal] = useState(false);
  const [debugPayload, setDebugPayload] = useState(null);
  const [downloadedIds, setDownloadedIds] = useState(() => {
    try {
      const saved = localStorage.getItem('quotation_downloaded_bills');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Helper function to get the appropriate token (RBAC token takes priority)
  const getAuthToken = () => {
    // Check for RBAC session first
    const rbacSessionStr = sessionStorage.getItem('rbac_session');
    if (rbacSessionStr) {
      try {
        const rbacSession = JSON.parse(rbacSessionStr);
        if (rbacSession.token) {
          return rbacSession.token;
        }
      } catch (err) {
        console.error('Error parsing RBAC session:', err);
      }
    }
    // Fall back to main client token
    return localStorage.getItem('voicebot_access_token');
  };

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    lead_status: '',
    source: '',
    call_type: '',
    interest_level: '',
    follow_up_required: '',
    date_from: '',
    date_to: '',
    assigned_to: assignedOnly ? (rbacRoleId || user?.rbac_role_id || user?.id || '') : '',
    page: 1,
    limit: 20
  });
  const [pbxConfig, setPbxConfig] = useState({ pbx_api_url: '', pbx_api_key: '', pbx_extension: '' });
  const [isSyncingCallLogs, setIsSyncingCallLogs] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();

      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      // Enforce assignedOnly logic just in case user object was late to load
      if (assignedOnly && (rbacRoleId || user?.id) && !params.get('assigned_to')) {
        params.set('assigned_to', rbacRoleId || user?.id);
      }

      // Resolve effective scope: new prop takes priority, then legacy viewMode alias
      const effectiveScope = scope || (viewMode === 'hierarchy-readonly' ? 'hierarchy' : null);
      if (effectiveScope === 'hierarchy') {
        params.set('scope', 'hierarchy');
      } else if (viewMode === 'hierarchy-readonly') {
        params.set('view_mode', 'hierarchy'); // backwards compat
      }

      const response = await axios.get(`${API_BASE_URL}/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setLeads(response.data.leads);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (error.response?.status !== 401) {
        // Don't show alert for auth errors
      }
    } finally {
      setLoading(false);
    }
  }, [filters, user, assignedOnly]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();

      // Forward the same filters used by fetchLeads (minus page/limit)
      // so the stat cards always match what's actually filtered in the table.
      const statFilterKeys = [
        'search', 'lead_status', 'source', 'call_type',
        'interest_level', 'follow_up_required', 'date_from', 'date_to'
      ];
      statFilterKeys.forEach((key) => {
        if (filters[key]) params.append(key, filters[key]);
      });

      if (assignedOnly && (rbacRoleId || user?.id) && !params.get('assigned_to')) {
        params.set('assigned_to', rbacRoleId || user?.id);
      }

      const effectiveScope = scope || (viewMode === 'hierarchy-readonly' ? 'hierarchy' : null);
      if (effectiveScope === 'hierarchy') {
        params.set('scope', 'hierarchy');
      } else if (viewMode === 'hierarchy-readonly') {
        params.set('view_mode', 'hierarchy');
      }

      const response = await axios.get(`${API_BASE_URL}/leads/statistics?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStatistics(response.data.statistics);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, [filters, assignedOnly, rbacRoleId, user, scope, viewMode]);

  // Fetch lead details with activities
  const fetchLeadDetails = async (leadId) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_BASE_URL}/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log('📋 Lead Details Response:', response.data.lead);
        console.log('🔑 assigned_by_role_id:', response.data.lead.assigned_by_role_id);
        console.log('👤 assigned_by_role_username:', response.data.lead.assigned_by_role_username);
        console.log('🏷️ assigned_by_role_name:', response.data.lead.assigned_by_role_name);
        setSelectedLead(response.data.lead);
        setActivities(response.data.lead.activities || []);
        setShowLeadModal(true);

        // Fetch quotations for this lead
        fetchLeadQuotations(leadId);
        // Fetch followups for this lead
        fetchFollowups(leadId);
        // Ensure templates are loaded
        if (whatsappTemplates.length === 0) {
          fetchWhatsAppTemplates();
        }
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
      if (error.response?.status !== 401) {
        alert('Failed to fetch lead details');
      }
    }
  };

  // Fetch quotations for a specific lead
  const fetchLeadQuotations = async (leadId) => {
    try {
      setLoadingQuotations(true);
      console.log(`📥 Fetching quotations for lead: ${leadId}`);
      const response = await quotationsAPI.getByLeadId(leadId);
      const quotations = response.data?.quotations || [];
      setLeadQuotations(quotations);
      console.log(`✅ Found ${quotations.length} quotations for lead`);
    } catch (error) {
      console.error('Error fetching lead quotations:', error);
      setLeadQuotations([]);
    } finally {
      setLoadingQuotations(false);
    }
  };

  const fetchFollowups = async (leadId) => {
    try {
      setLoadingFollowups(true);
      const token = getAuthToken();
      const response = await axios.get(
        `${API_BASE_URL}/leads/${leadId}/followups`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFollowups(response.data.followups || []);
    } catch (error) {
      console.error('Error fetching followups:', error);
    } finally {
      setLoadingFollowups(false);
    }
  };

  const fetchWhatsAppTemplates = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_BASE_URL}/whatsapp/meta-templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && response.data.data) {
        // Filter for APPROVED templates only
        const approvedTemplates = response.data.data.filter(
          t => t.status && t.status.toUpperCase() === 'APPROVED'
        );
        setWhatsappTemplates(approvedTemplates);
      }
    } catch (error) {
      console.error('Error fetching whatsapp templates:', error);
    }
  };

  const handleScheduleFollowup = async (e) => {
    e.preventDefault();
    if (!newFollowup.scheduled_date || !newFollowup.scheduled_time) {
      toast.error('Date and time are required');
      return;
    }

    if (newFollowup.mode === 'whatsapp' && !newFollowup.template_name) {
      toast.error('WhatsApp template is required');
      return;
    }

    try {
      setSchedulingFollowup(true);
      const token = getAuthToken();
      const scheduledDateTime = new Date(`${newFollowup.scheduled_date}T${newFollowup.scheduled_time}`).toISOString();

      await axios.post(
        `${API_BASE_URL}/leads/${selectedLead.id}/schedule-followup`,
        {
          scheduled_at: scheduledDateTime,
          mode: newFollowup.mode,
          template_name: newFollowup.mode === 'whatsapp' ? newFollowup.template_name : null,
          template_language: 'en'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Follow-up scheduled successfully');
      setNewFollowup({
        scheduled_date: '',
        scheduled_time: '',
        mode: 'whatsapp',
        template_name: ''
      });
      fetchFollowups(selectedLead.id);
      fetchLeadDetails(selectedLead.id); // refresh activities
    } catch (error) {
      console.error('Error scheduling followup:', error);
      toast.error(error.response?.data?.message || 'Failed to schedule follow-up');
    } finally {
      setSchedulingFollowup(false);
    }
  };

  const handleCancelFollowup = async (followupId) => {
    if (!window.confirm('Are you sure you want to cancel this follow-up?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(
        `${API_BASE_URL}/leads/${selectedLead.id}/followups/${followupId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Follow-up cancelled');
      fetchFollowups(selectedLead.id);
    } catch (error) {
      console.error('Error cancelling followup:', error);
      toast.error('Failed to cancel follow-up');
    }
  };

  const sendNewChatTemplate = async () => {
    try {
      const number = newChatPhone.replace(/[^\d]/g, '');
      if (!number) {
        setNewChatToast({ type: 'error', msg: 'Please enter a valid phone number' });
        return;
      }

      const fullNumber = `${selectedCountry.code.replace('+', '')}${number}`;
      setNewChatSending(true);
      setNewChatToast(null);

      const rbacSessionStr = sessionStorage.getItem('rbac_session');
      let currentClientId = user?.client_id || user?.user_metadata?.client_id || user?.id;
      if (rbacSessionStr) {
        try {
          const rbacSession = JSON.parse(rbacSessionStr);
          if (rbacSession.client_id) currentClientId = rbacSession.client_id;
        } catch (e) { }
      }

      const payload = {
        user_phone: fullNumber,
        template_name: selectedTemplateForChat || 'tecnvi_he',
        template_language: 'en',
        template_params: [],
        client_id: currentClientId,
        agent_name: user?.name || user?.email || 'Agent',
        session_id: null,
      };

      const response = await fetch(`${AGENT_API_URL}/send-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        let errMsg = 'Failed to send message';
        if (errData.detail) {
          if (Array.isArray(errData.detail)) {
            errMsg = errData.detail.map(d => `${d.loc ? d.loc.join('.') : 'error'}: ${d.msg}`).join(', ');
          } else if (typeof errData.detail === 'string') {
            errMsg = errData.detail;
          } else {
            errMsg = JSON.stringify(errData.detail);
          }
        } else if (errData.message) {
          errMsg = typeof errData.message === 'string' ? errData.message : JSON.stringify(errData.message);
        }
        throw new Error(errMsg);
      }

      // Log activity if we know which lead it is
      if (selectedLead && selectedLead.id) {
        const token = getAuthToken();
        await axios.post(
          `${API_BASE_URL}/leads/${selectedLead.id}/activities`,
          {
            type: 'comment',
            notes: `Initiated WhatsApp chat using template: ${selectedTemplateForChat || 'tecnvi_he'}`
          },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(e => console.error('Activity log error:', e)); // Ignore errors here, just best effort log
        fetchLeadDetails(selectedLead.id);
      }

      setNewChatToast({ type: 'success', msg: 'Message sent successfully! Session opened.' });
      setTimeout(() => {
        setShowNewChatModal(false);
        setNewChatPhone('');
        setNewChatPhoneEdited(false);
        setNewChatToast(null);
      }, 2000);

    } catch (error) {
      setNewChatToast({ type: 'error', msg: error.message || 'Error sending message' });
    } finally {
      setNewChatSending(false);
    }
  };

  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const token = getAuthToken();
      await axios.put(
        `${API_BASE_URL}/leads/${leadId}`,
        { lead_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Lead status updated successfully');
      fetchLeads();
      fetchStatistics();

      if (selectedLead?.id === leadId) {
        fetchLeadDetails(leadId);
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      if (error.response?.status !== 401) {
        alert('Failed to update lead status');
      }
    }
  };

  // Convert lead to customer
  const convertLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to convert this lead to a customer?')) {
      return;
    }

    try {
      const token = getAuthToken();
      await axios.post(
        `${API_BASE_URL}/leads/${leadId}/convert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Lead converted to customer successfully');
      fetchLeads();
      fetchStatistics();
      setShowLeadModal(false);
    } catch (error) {
      console.error('Error converting lead:', error);
      alert(error.response?.data?.message || 'Failed to convert lead');
    }
  };

  // Add interaction/comment to lead
  const addInteraction = async () => {
    if (!newInteraction.notes.trim()) {
      toast.error('Please enter a comment or note');
      return;
    }

    setSubmittingInteraction(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_BASE_URL}/leads/${selectedLead.id}/interactions`,
        {
          activity_type: newInteraction.type,
          notes: newInteraction.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Interaction added successfully');
        // Reset form
        setNewInteraction({ type: 'comment', notes: '' });
        // Refresh lead details to show new interaction
        fetchLeadDetails(selectedLead.id);
      }
    } catch (error) {
      console.error('Error adding interaction:', error);
      toast.error('Failed to add interaction');
    } finally {
      setSubmittingInteraction(false);
    }
  };

  // Reclaim lead from child
  const handleReclaimLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to reclaim this lead back to your pool?')) return;
    try {
      const token = getAuthToken();
      await axios.post(
        `${API_BASE_URL}/leads/${leadId}/reclaim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead reclaimed successfully');
      fetchLeads();
    } catch (error) {
      console.error('Error reclaiming lead:', error);
      toast.error(error.response?.data?.error || 'Failed to reclaim lead');
    }
  };

  // Fetch products for quotation
  const fetchProducts = useCallback(async () => {
    try {
      console.log('📥 Fetching products...');
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const productList = data.products || data.data?.products || [];
        setProducts(productList);
        console.log(`✅ Loaded ${productList.length} products`);
      } else {
        console.warn('⚠️ Products fetch failed:', response.status);
      }
    } catch (error) {
      console.error('❌ Products error:', error);
    }
  }, []);

  // Fetch services for quotation
  const fetchServices = useCallback(async () => {
    try {
      console.log('📥 Fetching services...');
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/services`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const serviceList = data.services || data.data?.services || [];
        setServices(serviceList);
        console.log(`✅ Loaded ${serviceList.length} services`);
      } else {
        console.warn('⚠️ Services fetch failed:', response.status);
      }
    } catch (error) {
      console.error('❌ Services error:', error);
    }
  }, []);

  // Open quotation modal
  const handleCreateQuotation = () => {
    setShowQuotationModal(true);
    // Fetch products and services when opening modal
    fetchProducts();
    fetchServices();
  };

  // Helper function to format quotation status
  const getQuotationStatusBadge = (status) => {
    const statusColors = {
      'draft': { bg: '#f3f4f6', color: '#374151', label: 'Draft' },
      'sent': { bg: '#dbeafe', color: '#1e40af', label: 'Sent' },
      'accepted': { bg: '#d1fae5', color: '#065f46', label: 'Accepted' },
      'rejected': { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
      'expired': { bg: '#fef3c7', color: '#92400e', label: 'Expired' },
      'converted': { bg: '#e0e7ff', color: '#3730a3', label: 'Converted' }
    };

    const statusStyle = statusColors[status] || statusColors['draft'];

    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '0px',
        fontSize: '11px',
        fontWeight: '600',
        backgroundColor: statusStyle.bg,
        color: statusStyle.color,
        textTransform: 'uppercase'
      }}>
        {statusStyle.label}
      </span>
    );
  };

  // Quotation action handlers
  const handleViewQuotation = async (quotation) => {
    try {
      const response = await quotationsAPI.getById(quotation.id);
      if (response.success && response.data?.quotation) {
        setViewingQuotation(response.data.quotation);
        setShowViewModal(true);
      } else {
        toast.error('Failed to load quotation details');
      }
    } catch (error) {
      console.error('View quotation error:', error);
      toast.error('Failed to load quotation details');
    }
  };

  const handleEditQuotation = (quotation) => {
    setEditingQuotation(quotation);
    setShowEditModal(true);
    // Fetch products and services when opening edit modal
    fetchProducts();
    fetchServices();
  };

  const handleDeleteQuotation = async (quotationId, quotationNumber) => {
    if (!window.confirm(`Delete quotation ${quotationNumber}?`)) return;
    try {
      await quotationsAPI.delete(quotationId);
      toast.success('Quotation deleted successfully');
      if (selectedLead?.id) {
        fetchLeadQuotations(selectedLead.id);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error?.message || 'Failed to delete quotation');
    }
  };

  const handleSendQuotation = async (quotationId, quotationNumber) => {
    try {
      await quotationsAPI.updateStatus(quotationId, 'sent');
      toast.success(`Quotation ${quotationNumber} sent successfully!`);
      if (selectedLead?.id) {
        fetchLeadQuotations(selectedLead.id);
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Failed to send quotation');
    }
  };

  const handleSendQuotationEmail = async (quotation) => {
    const quotationId = quotation.id;
    const quotationNumber = quotation.quotation_number || quotation.quotationNumber;

    if (!quotation.customer_email && !quotation.customerEmail) {
      toast.error('Customer email not found in quotation');
      return;
    }

    const validUntil = quotation.valid_until || quotation.validUntil;
    if (validUntil && new Date(validUntil) < new Date()) {
      toast.error('Cannot send email for expired quotation');
      return;
    }

    const customerEmail = quotation.customer_email || quotation.customerEmail;
    const confirmMessage = `Send quotation ${quotationNumber} to ${customerEmail}?\n\nThe email will include interactive Accept/Reject buttons valid until ${validUntil ? new Date(validUntil).toLocaleDateString('en-IN') : 'N/A'}.`;

    if (!window.confirm(confirmMessage)) return;

    setSendingEmailId(quotationId);
    const toastId = toast.loading('📧 Sending quotation email...');

    try {
      const response = await quotationsAPI.sendEmail(quotationId);
      if (response.success) {
        toast.success(`✅ Quotation email sent to ${customerEmail}!`, { id: toastId, duration: 5000 });
        if (selectedLead?.id) {
          fetchLeadDetails(selectedLead.id);
          fetchLeadQuotations(selectedLead.id);
        }
      } else {
        toast.error('Failed to send quotation email', { id: toastId });
      }
    } catch (error) {
      console.error('Send quotation email error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send quotation email';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleSendQuotationWhatsApp = async (quotation) => {
    const quotationId = quotation.id;
    const quotationNumber = quotation.quotation_number || quotation.quotationNumber;
    const customerPhone = quotation.customer_phone || quotation.customerPhone || quotation.customer_mobile;

    if (!customerPhone) {
      toast.error('No phone number found on this quotation');
      return;
    }

    const confirmMessage = `Send quotation ${quotationNumber} to ${quotation.customer_name} via WhatsApp?\n\nThe message will include interactive Accept/Reject buttons.`;
    if (!window.confirm(confirmMessage)) return;

    setSendingWhatsappId(quotationId);
    const toastId = toast.loading('📱 Sending WhatsApp quotation...');

    try {
      const data = await quotationsAPI.sendWhatsApp(quotationId, 'leads');

      if (data.success) {
        toast.success(`✅ WhatsApp quotation sent to ${quotation.customer_name}!`, { id: toastId, duration: 5000 });
        if (selectedLead?.id) {
          fetchLeadDetails(selectedLead.id);
          fetchLeadQuotations(selectedLead.id);
        }
      } else {
        toast.error(data.message || 'Failed to send WhatsApp quotation', { id: toastId });
      }
    } catch (error) {
      console.error('Send WhatsApp quotation error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send WhatsApp quotation';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setSendingWhatsappId(null);
    }
  };

  const handleConvertToOrder = async (quotationId, quotationNumber) => {
    const confirmMessage = `Convert quotation ${quotationNumber} to order?\n\nThis will create a new order with all items from this quotation.`;

    if (!window.confirm(confirmMessage)) return;

    const toastId = toast.loading('Converting quotation to order...');

    try {
      const response = await quotationsAPI.convertToOrder(quotationId);
      if (response.success || response.data?.success) {
        const orderNumber = response.data?.orderNumber || response.orderNumber || 'New Order';
        toast.success(`✅ Quotation converted successfully!\nOrder Number: ${orderNumber}`, { id: toastId, duration: 5000 });
        if (selectedLead?.id) {
          fetchLeadQuotations(selectedLead.id);
        }
      } else {
        toast.error('Failed to convert quotation', { id: toastId });
      }
    } catch (error) {
      console.error('Convert to order error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to convert quotation to order';
      toast.error(errorMessage, { id: toastId });
    }
  };

  const handleBillDownload = async (quotationId, quotationNumber) => {
    if (downloadedIds.has(quotationId)) {
      return;
    }

    setDownloadingId(quotationId);
    const toastId = toast.loading('📄 Generating Bill PDF...');

    try {
      const result = await quotationsAPI.downloadPDF(quotationId);
      console.log('✅ PDF download successful:', result);

      setDownloadedIds(prev => {
        const updated = new Set(prev);
        updated.add(quotationId);
        try {
          localStorage.setItem('quotation_downloaded_bills', JSON.stringify([...updated]));
        } catch (e) {
          console.error('Failed to save to localStorage:', e);
        }
        return updated;
      });

      toast.success(`✅ ${quotationNumber} bill downloaded successfully!`, { id: toastId });
    } catch (error) {
      console.error('❌ PDF download failed:', error);
      toast.error(error.message || 'Failed to download bill. Please try again.', { id: toastId });
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchPbxConfig = async () => {
    try {
      const response = await adminRequirementsAPI.getAny();
      const data = response.data.data || response.data;
      setPbxConfig({
        pbx_api_url: data.pbx_api_url || '',
        pbx_api_key: data.pbx_api_key || '',
        pbx_extension: data.pbx_extension || ''
      });
    } catch (error) {
      console.error('Error fetching PBX config:', error);
    }
  };

  const syncCallLogs = async () => {
    if (!pbxConfig.pbx_api_url) {
      toast.error('PBX URL not configured. Please set telephony requirements first.');
      return;
    }

    if (!pbxConfig.pbx_api_key) {
      toast.error('PBX API key not available. Cannot fetch call records.');
      return;
    }

    setIsSyncingCallLogs(true);
    try {
      const result = await pbxCallAPI.syncCdrToCallLogs(pbxConfig.pbx_api_url, pbxConfig.pbx_api_key, {
        limit: 100
      });

      const synced = result?.synced ?? (result?.records?.length ?? 0);
      toast.success(`Fetched and saved ${synced} PBX call record(s).`);
      fetchLeads();
      fetchStatistics();
    } catch (error) {
      console.error('Error syncing call logs:', error);
      toast.error(error.message || 'Failed to fetch PBX call records.');
    } finally {
      setIsSyncingCallLogs(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchStatistics();
    fetchPbxConfig();
  }, [filters, fetchLeads, fetchStatistics, user]);

  useEffect(() => {
    const handleLeadsUpdated = () => {
      fetchLeads();
      fetchStatistics();
    };

    window.addEventListener('leadsUpdated', handleLeadsUpdated);
    return () => {
      window.removeEventListener('leadsUpdated', handleLeadsUpdated);
    };
  }, [fetchLeads]);

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'badge-new';
      case 'contacted': return 'badge-contacted';
      case 'replied': return 'badge-replied';
      case 'qualified': return 'badge-qualified';
      case 'converted': return 'badge-converted';
      case 'lost': return 'badge-lost';
      default: return 'badge-default';
    }
  };

  // Get source icon
  const getSourceIcon = (source) => {
    switch (source) {
      case 'call_log': return <Phone size={16} />;
      case 'whatsapp': return <MessageSquare size={16} />;
      case 'chatbot': return <Bot size={16} />;
      case 'manual': return <UserPlus size={16} />;
      default: return <Users size={16} />;
    }
  };

  // Get call type badge style
  const getCallTypeBadge = (callType) => {
    if (!callType) return null;
    const isInbound = callType === 'inbound';
    return (
      <span className={`call-type-badge ${isInbound ? 'badge-inbound' : 'badge-outbound'}`}>
        {isInbound ? '📞 Inbound' : '📲 Outbound'}
      </span>
    );
  };

  const totalLost = statistics?.total_lost ?? statistics?.lost_leads ?? statistics?.lost ?? statistics?.lost_count ??
    (leads.filter((lead) => String(lead.lead_status || '').toLowerCase() === 'lost').length || 0);

  const totalFollowUp = statistics?.total_follow_up ?? statistics?.follow_up_leads ?? statistics?.needs_follow_up ?? statistics?.follow_up_count ??
    (leads.filter((lead) =>
      lead.follow_up_required === true ||
      String(lead.follow_up_required || '').toLowerCase() === 'true' ||
      String(lead.lead_status || '').toLowerCase() === 'new'
    ).length || 0);

  const totalReplied = statistics?.replied_leads ?? statistics?.total_replied ?? statistics?.replied ?? statistics?.replied_count ??
    (leads.filter((lead) => String(lead.lead_status || '').toLowerCase() === 'replied').length || 0);

  return (
    <div className={`lead-management ${embedded ? 'embedded' : ''} lead-management-container`}>
      {/* ── Full Hierarchy Scope Banner (scope=hierarchy, no specific role) ── */}
      {scope === 'hierarchy' && (
        <div style={{
          background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
          padding: '12px 20px',
          borderRadius: '0px',
          marginBottom: '20px',
          border: '1px solid #c4b5fd',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '18px' }}>📊</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '700', color: '#5b21b6', fontSize: '14px' }}>Viewing Full Team Scope</div>
            <div style={{ color: '#6d28d9', fontSize: '12px', marginTop: '2px' }}>
              Read-only · All downstream leads across your entire hierarchy
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', fontWeight: '600', color: '#7c3aed' }}>
            <span>Total: {statistics?.total_leads ?? '…'} leads</span>
          </div>
        </div>
      )}
      {/* ── Direct Child View Banner (viewMode=hierarchy-readonly, specific role) ── */}
      {viewMode === 'hierarchy-readonly' && (
        <div style={{
          backgroundColor: '#fef3c7',
          padding: '10px 20px',
          borderRadius: '0px',
          marginBottom: '20px',
          border: '1px solid #fde68a',
          color: '#92400e',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: '600'
        }}>
          <Eye size={16} />
          {roleName ? `Viewing ${roleName}'s Leads` : 'Viewing Child Leads'}
          <span style={{ fontWeight: '400', marginLeft: '4px', fontSize: '13px' }}>(Read Only)</span>
          {statistics?.total_leads != null && (
            <span style={{ marginLeft: 'auto', fontWeight: '700' }}>{statistics.total_leads} leads</span>
          )}
        </div>
      )}
      {/* Statistics Cards */}
      {statistics && (
        <div className="lead-stats-grid">
          <div className="stat-card">
            <div className="stat-card-icon-wrapper">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Leads</div>
              <div className="stat-value">{statistics.total_leads || 0}</div>
              <div className="stat-description">
                <Users size={14} />
                Active prospects
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-icon-wrapper">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Converted</div>
              <div className="stat-value">{statistics.converted_leads || 0}</div>
              <div className="stat-description">
                <CheckCircle size={14} />
                Qualified prospects
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-icon-wrapper">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">New Leads</div>
              <div className="stat-value">{statistics.new_leads || 0}</div>
              <div className="stat-description">
                <Clock size={14} />
                Requires follow-up
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-icon-wrapper">
              <AlertTriangle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Lost</div>
              <div className="stat-value">{totalLost}</div>
              <div className="stat-description">
                <AlertTriangle size={14} />
                Closed unsuccessful
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-icon-wrapper">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Follow Up</div>
              <div className="stat-value">{totalFollowUp}</div>
              <div className="stat-description">
                <TrendingUp size={14} />
                Pending outreach
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-icon-wrapper">
              <Mail size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Replied</div>
              <div className="stat-value">{totalReplied}</div>
              <div className="stat-description">
                <Mail size={14} />
                Requires attention
              </div>
            </div>
          </div>

          {/* Chatbot Leads Stat */}
          {statistics.chatbot_leads > 0 && (
            <div className="stat-card">
              <div className="stat-card-icon-wrapper">
                <Bot size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{statistics.chatbot_leads || 0}</div>
                <div className="stat-label">Chatbot Leads</div>
              </div>
            </div>
          )}

          {/* New: Inbound/Outbound Stats */}
          {(statistics.inbound_call_leads || statistics.outbound_call_leads) && (
            <>
              <div className="stat-card">
                <div className="stat-card-icon-wrapper">
                  <Phone size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{statistics.inbound_call_leads || 0}</div>
                  <div className="stat-label">Inbound Calls</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-card-icon-wrapper">
                  <Phone size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{statistics.outbound_call_leads || 0}</div>
                  <div className="stat-label">Outbound Calls</div>
                </div>
              </div>
            </>
          )}

          {/* New: Unassigned Leads Stats (Admin/Main only) */}
          {!assignedOnly && statistics.unassigned_leads !== undefined && (
            <div className="stat-card">
              <div className="stat-card-icon-wrapper">
                <Users size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{statistics.unassigned_leads || 0}</div>
                <div className="stat-label">Unassigned Leads</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="lead-filters">
        <div className="filters-compact-row">
          <select
            className="filter-select-compact"
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value, page: 1 })}
          >
            <option value="">📱 All Sources</option>
            <option value="call_log">📞 Call Log</option>
            <option value="whatsapp">💬 WhatsApp</option>
            <option value="chatbot">Chatbot</option>
            <option value="manual">✏️ Manual</option>
          </select>

          <select
            className="filter-select-compact"
            value={filters.call_type}
            onChange={(e) => setFilters({ ...filters, call_type: e.target.value, page: 1 })}
          >
            <option value="">📲 All Calls</option>
            <option value="inbound">📞 Inbound</option>
            <option value="outbound">📤 Outbound</option>
          </select>

          <select
            className="filter-select-compact"
            value={filters.lead_status}
            onChange={(e) => setFilters({ ...filters, lead_status: e.target.value, page: 1 })}
          >
            <option value="">🎯 All Status</option>
            <option value="new">🆕 New</option>
            <option value="contacted">📧 Contacted</option>
            <option value="replied">📩 Replied</option>
            <option value="qualified">⭐ Qualified</option>
            <option value="converted">✅ Converted</option>
            <option value="lost">❌ Lost</option>
          </select>

          <select
            className="filter-select-compact"
            value={filters.interest_level}
            onChange={(e) => setFilters({ ...filters, interest_level: e.target.value, page: 1 })}
          >
            <option value="">💡 Interest</option>
            <option value="high">🔥 High</option>
            <option value="medium">⚡ Medium</option>
            <option value="low">❄️ Low</option>
          </select>

          <select
            className="filter-select-compact"
            value={filters.follow_up_required}
            onChange={(e) => setFilters({ ...filters, follow_up_required: e.target.value, page: 1 })}
          >
            <option value="">🔔 Follow-up</option>
            <option value="true">✓ Required</option>
            <option value="false">✗ Not Needed</option>
          </select>
        </div>

        <div className="filters-compact-row filters-actions-row">
          <input
            type="date"
            className="filter-date-compact"
            placeholder="From"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value, page: 1 })}
            title="Date From"
          />

          <input
            type="date"
            className="filter-date-compact"
            placeholder="To"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value, page: 1 })}
            title="Date To"
          />

          <button
            className="btn-clear-compact"
            onClick={() => setFilters({
              search: '',
              lead_status: '',
              source: '',
              call_type: '',
              interest_level: '',
              follow_up_required: '',
              date_from: '',
              date_to: '',
              page: 1,
              limit: 20
            })}
            title="Clear all filters"
          >
            🔄
          </button>

          {/* Fetch Call Records button removed from UI (frontend-only) */}

          {!assignedOnly && viewMode !== 'hierarchy-readonly' && scope !== 'hierarchy' && !readonly && (
            <>
              <div className="filters-actions-spacer" />
              <button
                className="btn btn-primary lead-action-button"
                onClick={() => setShowAddLeadModal(true)}
                style={{ padding: '6px 12px', height: '38px', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#10b981', borderColor: '#10b981' }}
                title="Add New Lead Manually"
              >
                <UserPlus size={16} /> Add Lead
              </button>
              <button
                className="btn btn-primary lead-action-button"
                onClick={() => setShowBulkUploadModal(true)}
                style={{ padding: '6px 12px', height: '38px', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
                title="Bulk Upload Leads from Excel"
              >
                <Upload size={16} /> Bulk Upload
              </button>
              <button
                className="btn btn-primary lead-action-button"
                onClick={() => setShowAssignmentModal(true)}
                style={{ padding: '6px 12px', height: '38px', display: 'flex', alignItems: 'center', gap: '6px' }}
                title="Assign Leads to Users"
              >
                <Users size={16} /> Assign Leads
              </button>
            </>
          )}
        </div>

        <div className="maya-lead-management-search-wrapper-unique">
          <Search className="maya-lead-management-search-icon-unique" />
          <input
            type="text"
            placeholder="Search by mobile number or name..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            className="maya-lead-management-search-input-unique"
          />
        </div>
      </div>

      {/* Leads Table */}
      <div className="leads-table-container">
        {loading ? (
          <div className="loading-spinner">Loading leads...</div>
        ) : leads.length === 0 ? (
          <div className="no-leads">
            <Users size={48} />
            <p>No leads found</p>
            <small>Leads will automatically appear from call logs and WhatsApp chats</small>
          </div>
        ) : (
          <table className="leads-table">
            <thead className="leads-table-header">
              <tr>
                <th>Mobile Number</th>
                <th>Customer Name</th>
                <th>Source</th>
                <th>Call Type</th>
                <th>Status</th>
                {!assignedOnly && <th>Assigned To</th>}
                <th>Interest Level</th>
                <th>Last Contact</th>
                <th>Follow-up</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td>
                    <div className="mobile-number">
                      {getDisplayPhoneNumber(lead.mobile_number)}
                    </div>
                  </td>
                  <td>
                    <div className="customer-info-cell">
                      <div className="customer-name">{lead.customer_name || '-'}</div>
                    </div>
                  </td>
                  <td>
                    <div className="source-badge">
                      {getSourceIcon(lead.source)}
                      <span>{lead.source}</span>
                    </div>
                  </td>
                  <td>
                    {lead.call_type ? getCallTypeBadge(lead.call_type) : <span className="no-data">-</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`status-badge ${getStatusColor(lead.lead_status)}`}>
                      {lead.lead_status}
                    </span>
                  </td>
                  {!assignedOnly && (
                    <td>
                      {lead.assigned_to ? (
                        <span className="assigned-user" title={lead.assigned_to_role_name || ''}>
                          {lead.assigned_to_username || lead.assigned_to_role_name || lead.assigned_to}
                        </span>
                      ) : (
                        <span className="no-data">Unassigned</span>
                      )}
                    </td>
                  )}
                  <td>
                    <div className="interest-level">
                      <div className="interest-bar">
                        <div
                          className="interest-fill"
                          style={{ width: `${(lead.interest_level || 0) * 10}%` }}
                        />
                      </div>
                      <span>{lead.interest_level || 0}/10</span>
                    </div>
                  </td>
                  <td>
                    {lead.last_contact_date
                      ? new Date(lead.last_contact_date).toLocaleDateString()
                      : '-'
                    }
                  </td>
                  <td style={{ minWidth: '130px' }}>
                    {lead.follow_up_required ? (
                      <span className="follow-up-badge">Required</span>
                    ) : (
                      <span className="no-follow-up">-</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-view"
                        onClick={() => fetchLeadDetails(lead.id)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>

                      {/* Reclaim: only for direct-child view (viewMode), not in full hierarchy scope */}
                      {viewMode === 'hierarchy-readonly' && scope !== 'hierarchy' && (
                        <button
                          className="btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReclaimLead(lead.id);
                          }}
                          title="Reclaim Lead"
                          style={{ color: '#ef4444' }}
                        >
                          <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
                        </button>
                      )}

                      {lead.lead_status === 'qualified' && viewMode !== 'hierarchy-readonly' && scope !== 'hierarchy' && !readonly && (
                        <button
                          className="btn-icon btn-convert"
                          onClick={() => convertLead(lead.id)}
                          title="Convert to Customer"
                        >
                          <UserPlus size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            disabled={pagination.page === 1}
            onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            disabled={pagination.page === pagination.pages}
            onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
          >
            Next
          </button>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showLeadModal && selectedLead && (
        <div className="modal-overlay" onClick={() => setShowLeadModal(false)}>
          <div className="modal-content lead-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Lead Details</h3>
              <button onClick={() => setShowLeadModal(false)}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="lead-info-grid">
                <div className="info-item">
                  <label>Mobile Number</label>
                  <div className="info-value">{getDisplayPhoneNumber(selectedLead.mobile_number)}</div>
                </div>
                <div className="info-item">
                  <label>Customer Name</label>
                  <div className="info-value">{selectedLead.customer_name || 'Not provided'}</div>
                </div>
                <div className="info-item">
                  <label>Source</label>
                  <div className="info-value source-badge">
                    {getSourceIcon(selectedLead.source)}
                    <span>{selectedLead.source === 'chatbot' ? '🤖 Chatbot' : selectedLead.source}</span>
                  </div>
                </div>
                {selectedLead.email && (
                  <div className="info-item">
                    <label>Email</label>
                    <div className="info-value">{selectedLead.email}</div>
                  </div>
                )}
                {selectedLead.referred_by && (
                  <div className="info-item">
                    <label>Referred By</label>
                    <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        borderRadius: '0px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        👤 {selectedLead.referred_by}
                      </span>
                    </div>
                  </div>
                )}
                <div className="info-item">
                  <label>Current Status</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span className={`status-badge ${getStatusColor(selectedLead.lead_status)}`}>
                      {selectedLead.lead_status}
                    </span>
                    <select
                      value={selectedLead.lead_status}
                      onChange={(e) => updateLeadStatus(selectedLead.id, e.target.value)}
                      className="status-select-dropdown"
                      style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '0px', border: '1px solid #ddd' }}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="replied">Replied</option>
                      <option value="qualified">Qualified</option>
                      <option value="converted">Converted</option>
                      <option value="lost">Lost</option>
                    </select>
                  </div>
                </div>
                <div className="info-item">
                  <label>Interest Level</label>
                  <div className="info-value">
                    <div className="interest-level">
                      <div className="interest-bar" style={{ width: '100px' }}>
                        <div
                          className="interest-fill"
                          style={{ width: `${(selectedLead.interest_level || 0) * 10}%` }}
                        />
                      </div>
                      <span>{selectedLead.interest_level || 0}/10</span>
                    </div>
                  </div>
                </div>
                <div className="info-item">
                  <label>First Contact</label>
                  <div className="info-value">
                    {new Date(selectedLead.first_contact_date).toLocaleString()}
                  </div>
                </div>
                <div className="info-item">
                  <label>Assigned To</label>
                  <div className="info-value">
                    {selectedLead.assigned_to ? (
                      <span className="assigned-user" title={selectedLead.assigned_to_role_name || ''}>
                        {selectedLead.assigned_to_username || selectedLead.assigned_to_role_name || 'Assigned'}
                      </span>
                    ) : (
                      <span className="no-data">Unassigned</span>
                    )}
                  </div>
                </div>
                {/* Show Assigned By if we have any assignment tracking info */}
                {selectedLead.assigned_to && (
                  <div className="info-item" style={{ border: '3px solid', padding: '10px', background: '#ffffff' }}>
                    <label style={{ fontWeight: 'bold', fontSize: '16px' }}>ASSIGNED BY</label>
                    <div className="info-value">

                      {selectedLead.assigned_by_role_id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '10px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '6px 6px',
                            borderRadius: '0px',
                            background: '#8b5cf6',
                            color: 'white',
                            fontSize: '13px',
                            fontWeight: '600'
                          }}>
                            👤 {selectedLead.assigned_by_role_name || 'Role'}
                          </span>
                          {selectedLead.assigned_by_role_username && (
                            <span style={{ fontSize: '12px', color: '#6c757d' }}>
                              Username: @{selectedLead.assigned_by_role_username}
                            </span>
                          )}
                          {!selectedLead.assigned_by_role_username && (
                            <span style={{ fontSize: '11px', color: '#999' }}>
                              ID: {selectedLead.assigned_by_role_id}
                            </span>
                          )}
                        </div>
                      ) : selectedLead.assigned_by_client_id ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 12px',
                          borderRadius: '0px',
                          background: '#f093fb',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          🏢 Main Client
                        </span>
                      ) : (
                        <span style={{ color: '#999', fontSize: '13px' }}>Not tracked</span>
                      )}
                    </div>
                  </div>
                )}
                {selectedLead.assigned_at && (
                  <div className="info-item">
                    <label>Assigned On</label>
                    <div className="info-value" style={{ fontSize: '13px', color: '#6c757d' }}>
                      📅 {new Date(selectedLead.assigned_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                )}
                {selectedLead.call_type && (
                  <div className="info-item">
                    <label>Call Type</label>
                    <div className="info-value">{getCallTypeBadge(selectedLead.call_type)}</div>
                  </div>
                )}
                {selectedLead.call_duration_minutes > 0 && (
                  <div className="info-item">
                    <label>Call Duration</label>
                    <div className="info-value">{selectedLead.call_duration_minutes} minutes</div>
                  </div>
                )}
              </div>

              {/* Interest Summary Section */}
              {selectedLead.interest_summary && (
                <div className="info-section" style={{ marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '0px', border: '1px solid #e9ecef' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    💡 Interest Summary
                  </h4>
                  <p style={{ margin: 0, color: '#6c757d', lineHeight: '1.6' }}>{selectedLead.interest_summary}</p>
                </div>
              )}

              {/* Conversation Data Section */}
              {selectedLead.conversation_data && Object.keys(selectedLead.conversation_data).length > 0 && (
                <div className="info-section" style={{ marginTop: '15px', padding: '15px', background: '#f0f7ff', borderRadius: '0px', border: '1px solid #cce5ff' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#004085' }}>
                    💬 Conversation Data
                  </h4>
                  <div style={{ fontSize: '13px', color: '#004085' }}>
                    {Object.entries(selectedLead.conversation_data).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '8px', display: 'flex', gap: '10px' }}>
                        <strong style={{ minWidth: '120px', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}:
                        </strong>
                        <span>{typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

{/* Call Analysis Section */}
{selectedLead.call_analysis && Object.keys(selectedLead.call_analysis).length > 0 && (
  <div className="info-section" style={{ marginTop: '15px', padding: '15px', background: '#eff6ff', borderRadius: '0px', border: '1px solid #bfdbfe' }}>
    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
      📊 Call Analysis
    </h4>
    <div style={{ fontSize: '13px', color: '#1e40af' }}>
                    {Object.entries(selectedLead.call_analysis).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '8px', display: 'flex', gap: '10px' }}>
                        <strong style={{ minWidth: '120px', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}:
                        </strong>
                        <span>{typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Call Transcript Section */}
              {selectedLead.bot_conversation_data && (() => {
                const turns = parseTranscript(selectedLead.bot_conversation_data);
                return (
                  <div className="info-section" style={{ marginTop: '15px', padding: '15px', background: '#f0fdf4', borderRadius: '0px', border: '1px solid #bbf7d0' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Phone size={16} style={{ color: '#16a34a' }} />
                      Call Transcript
                    </h4>
                    <div style={{
                      maxHeight: '260px',
                      overflowY: 'auto',
                      background: 'white',
                      padding: '12px',
                      borderRadius: '0px',
                      border: '1px solid #d1fae5',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      {turns.length === 0 ? (
                        <div style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                          Transcript format not recognized — raw data below:
                          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '11px', marginTop: '6px' }}>
                            {JSON.stringify(selectedLead.bot_conversation_data, null, 2)}
                          </pre>
                        </div>
                      ) : (
                        turns.map((t, i) => {
                          const isBot = t.role.includes('bot') || t.role.includes('assistant') || t.role.includes('agent');
                          return (
                            <div key={i} style={{ display: 'flex', justifyContent: isBot ? 'flex-start' : 'flex-end' }}>
                              <div style={{
                                maxWidth: '75%',
                                padding: '8px 12px',
                                borderRadius: '0px',
                                fontSize: '13px',
                                lineHeight: '1.5',
                                background: isBot ? '#e0f2fe' : '#dcfce7',
                                color: isBot ? '#0369a1' : '#166534',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {t.text}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Call Recording Section */}
              {selectedLead.recording_url && (
                <div className="info-section" style={{ marginTop: '15px', padding: '15px', background: '#fdf4ff', borderRadius: '0px', border: '1px solid #f5d0fe' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#86198f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Phone size={16} style={{ color: '#a21caf' }} />
                    Call Recording
                  </h4>
                  <audio controls style={{ width: '100%' }} src={`${API_BASE_URL}/call-logs/recording-proxy?url=${encodeURIComponent(selectedLead.recording_url)}&token=${getAuthToken()}`} preload="metadata">
                    Your browser does not support the audio element.
                  </audio>
                  <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#a21caf' }}>
                    If the player doesn't load, your browser may be blocking this recording because it's served over an insecure (http://) connection.
                  </p>
                </div>
              )}

              {/* Notes Section */}
              {selectedLead.notes && (
                <div className="info-section" style={{ marginTop: '15px', padding: '15px', background: '#fff', borderRadius: '0px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                    📝 Notes
                  </h4>
                  <p style={{ margin: 0, color: '#6c757d', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{selectedLead.notes}</p>
                </div>
              )}

              {/* Add Interaction Form */}
              {!readonly && scope !== 'hierarchy' && viewMode !== 'hierarchy-readonly' && (
                <div className="info-section" style={{ marginTop: '20px', padding: '18px', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)', borderRadius: '0px', border: '2px solid #d1d9e6' }}>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '15px', fontWeight: '700', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MessageSquare size={18} style={{ color: '#8b5cf6' }} />
                    Add Interaction
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        value={newInteraction.type}
                        onChange={(e) => setNewInteraction({ ...newInteraction, type: e.target.value })}
                        style={{
                          padding: '10px 14px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '0px',
                          fontSize: '13px',
                          fontWeight: '500',
                          backgroundColor: 'white',
                          color: '#334155',
                          cursor: 'pointer',
                          minWidth: '180px',
                          outline: 'none',
                          transition: 'all 0.2s',
                          flex: 1
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                      >
                        <option value="comment">💬 Comment</option>
                        <option value="called">📞 Called</option>
                        <option value="emailed">📧 Emailed</option>
                        <option value="quotation_sent">📄 Quotation Sent</option>
                        <option value="meeting_scheduled">📅 Meeting Scheduled</option>
                        <option value="follow_up">🔔 Follow-up</option>
                        <option value="issue">⚠️ Issue/Problem</option>
                        <option value="resolved">✅ Resolved</option>
                      </select>
                    </div>
                    <textarea
                      value={newInteraction.notes}
                      onChange={(e) => setNewInteraction({ ...newInteraction, notes: e.target.value })}
                      placeholder="Add details about this interaction... (e.g., 'Called the lead but they didn't respond' or 'Sent quotation via email')"
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '0px',
                        fontSize: '13px',
                        resize: 'vertical',
                        backgroundColor: 'white',
                        color: '#334155',
                        outline: 'none',
                        transition: 'all 0.2s',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                      onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                    />
                    <button
                      onClick={addInteraction}
                      disabled={submittingInteraction || !newInteraction.notes.trim()}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: submittingInteraction || !newInteraction.notes.trim() ? '#cbd5e1' : '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: submittingInteraction || !newInteraction.notes.trim() ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        alignSelf: 'flex-start',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(139, 92, 246, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        if (!submittingInteraction && newInteraction.notes.trim()) {
                          e.target.style.backgroundColor = '#7c3aed';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(139, 92, 246, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = submittingInteraction || !newInteraction.notes.trim() ? '#cbd5e1' : '#8b5cf6';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(139, 92, 246, 0.2)';
                      }}
                    >
                      <Send size={14} />
                      {submittingInteraction ? 'Adding...' : 'Add Interaction'}
                    </button>
                  </div>
                </div>
              )}

              {/* Schedule Follow-up Section — Premium Glassmorphic */}
              {!readonly && scope !== 'hierarchy' && viewMode !== 'hierarchy-readonly' && (
                <div style={{
                  marginTop: '20px',
                  padding: '20px',
                  background: 'linear-gradient(135deg, rgba(253,244,255,0.6) 0%, rgba(255,255,255,0.85) 100%)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  borderRadius: '0px',
                  border: '1px solid rgba(219,39,119,0.15)',
                  boxShadow: '0 8px 32px rgba(131,24,67,0.07)',
                  transition: 'box-shadow 0.3s ease'
                }}>
                  {/* Section Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '0px',
                      background: 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(219,39,119,0.3)', flexShrink: 0
                    }}>
                      <Clock size={15} style={{ color: 'white' }} />
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#831843', letterSpacing: '0.01em' }}>
                        Schedule Follow-up
                      </h4>
                      <p style={{ margin: '1px 0 0 0', fontSize: '11px', color: '#be185d', opacity: 0.75 }}>
                        Automated WhatsApp or call reminder
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleScheduleFollowup} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {/* Date + Time row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{
                          fontSize: '10px', color: '#be185d', fontWeight: '700',
                          textTransform: 'uppercase', letterSpacing: '0.6px',
                          display: 'block', marginBottom: '5px'
                        }}>Date</label>
                        <input
                          type="date"
                          required
                          min={new Date().toISOString().split('T')[0]}
                          value={newFollowup.scheduled_date}
                          onChange={(e) => setNewFollowup({ ...newFollowup, scheduled_date: e.target.value })}
                          style={{
                            width: '100%', padding: '9px 12px', borderRadius: '0px',
                            border: '1px solid rgba(219,39,119,0.2)', outline: 'none',
                            background: 'rgba(255,255,255,0.8)', boxSizing: 'border-box',
                            fontSize: '13px', color: '#1f2937',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{
                          fontSize: '10px', color: '#be185d', fontWeight: '700',
                          textTransform: 'uppercase', letterSpacing: '0.6px',
                          display: 'block', marginBottom: '5px'
                        }}>Time</label>
                        <input
                          type="time"
                          required
                          value={newFollowup.scheduled_time}
                          onChange={(e) => setNewFollowup({ ...newFollowup, scheduled_time: e.target.value })}
                          style={{
                            width: '100%', padding: '9px 12px', borderRadius: '0px',
                            border: '1px solid rgba(219,39,119,0.2)', outline: 'none',
                            background: 'rgba(255,255,255,0.8)', boxSizing: 'border-box',
                            fontSize: '13px', color: '#1f2937',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                          }}
                        />
                      </div>
                    </div>

                    {/* Mode + Template row */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: newFollowup.mode === 'whatsapp' ? '1fr 1fr' : '1fr',
                      gap: '12px', minWidth: 0
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <label style={{
                          fontSize: '10px', color: '#be185d', fontWeight: '700',
                          textTransform: 'uppercase', letterSpacing: '0.6px',
                          display: 'block', marginBottom: '5px'
                        }}>Mode</label>
                        <select
                          value={newFollowup.mode}
                          onChange={(e) => setNewFollowup({ ...newFollowup, mode: e.target.value })}
                          style={{
                            width: '100%', padding: '9px 12px', borderRadius: '0px',
                            border: '1px solid rgba(219,39,119,0.2)', outline: 'none',
                            background: 'rgba(255,255,255,0.9)', boxSizing: 'border-box',
                            fontSize: '13px', color: '#1f2937', cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                          }}
                        >
                          <option value="whatsapp">💬 WhatsApp Message</option>
                          <option value="phone">📞 Phone Call</option>
                          <option value="email">📧 Email</option>
                        </select>
                      </div>

                      {newFollowup.mode === 'whatsapp' && (
                        <div style={{ minWidth: 0 }}>
                          <label style={{
                            fontSize: '10px', color: '#be185d', fontWeight: '700',
                            textTransform: 'uppercase', letterSpacing: '0.6px',
                            display: 'block', marginBottom: '5px'
                          }}>WhatsApp Template</label>
                          <select
                            required
                            value={newFollowup.template_name}
                            onChange={(e) => setNewFollowup({ ...newFollowup, template_name: e.target.value })}
                            style={{
                              width: '100%', maxWidth: '100%', padding: '9px 12px', borderRadius: '0px',
                              border: '1px solid rgba(219,39,119,0.2)', outline: 'none',
                              background: 'rgba(255,255,255,0.9)', boxSizing: 'border-box',
                              fontSize: '13px', color: '#1f2937', cursor: 'pointer',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                            }}
                          >
                            <option value="">Select a template...</option>
                            {whatsappTemplates.map(t => (
                              <option key={t.id || t.name} value={t.name}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={schedulingFollowup}
                      style={{
                        padding: '10px 20px',
                        background: schedulingFollowup
                          ? 'rgba(219,39,119,0.35)'
                          : 'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: schedulingFollowup ? 'not-allowed' : 'pointer',
                        alignSelf: 'flex-start',
                        transition: 'all 0.2s ease',
                        boxShadow: schedulingFollowup ? 'none' : '0 4px 14px rgba(219,39,119,0.3)',
                        letterSpacing: '0.01em'
                      }}
                    >
                      {schedulingFollowup ? '⏳ Scheduling...' : '✓ Schedule Follow-up'}
                    </button>
                  </form>

                  {/* Scheduled Follow-ups List */}
                  {followups.length > 0 && (
                    <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px dashed rgba(219,39,119,0.15)' }}>
                      <p style={{
                        fontSize: '10px', color: '#9d174d', fontWeight: '700',
                        textTransform: 'uppercase', letterSpacing: '0.7px',
                        margin: '0 0 10px 0'
                      }}>Pending Follow-ups</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {followups.filter(f => f.status === 'pending').map(f => (
                          <div key={f.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: 'rgba(255,255,255,0.75)',
                            backdropFilter: 'blur(8px)',
                            padding: '10px 14px', borderRadius: '0px',
                            border: '1px solid rgba(219,39,119,0.1)',
                            boxShadow: '0 2px 8px rgba(131,24,67,0.05)'
                          }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>
                                {new Date(f.scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', textTransform: 'capitalize' }}>
                                {f.mode === 'whatsapp' ? '💬' : f.mode === 'phone' ? '📞' : '📧'} {f.mode}
                                {f.mode === 'whatsapp' && f.template_name ? ` · ${f.template_name}` : ''}
                              </div>
                            </div>
                            {!readonly && scope !== 'hierarchy' && viewMode !== 'hierarchy-readonly' && (
                              <button
                                onClick={() => handleCancelFollowup(f.id)}
                                style={{
                                  border: 'none',
                                  background: 'rgba(254,226,226,0.8)',
                                  color: '#ef4444',
                                  padding: '5px 10px', borderRadius: '0px',
                                  fontSize: '11px', cursor: 'pointer', fontWeight: '600',
                                  transition: 'background 0.2s'
                                }}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        ))}
                        {followups.filter(f => f.status === 'pending').length === 0 && (
                          <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
                            No pending follow-ups scheduled.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}


              {/* Activity Timeline */}
              <div className="activity-timeline" style={{ marginTop: '20px' }}>
                <h4 style={{
                  margin: '0 0 20px 0',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  <History size={20} style={{ color: '#8b5cf6' }} />
                  Interaction Roadmap
                </h4>
                {activities.length === 0 ? (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    background: '#f8f9fa',
                    borderRadius: '0px',
                    border: '2px dashed #cbd5e1'
                  }}>
                    <History size={36} style={{ color: '#94a3b8', marginBottom: '12px' }} />
                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>No interactions yet</p>
                    <p style={{ margin: '8px 0 0 0', color: '#94a3b8', fontSize: '12px' }}>Add your first interaction above to start tracking</p>
                  </div>
                ) : (
                  <div className="timeline" style={{ position: 'relative', paddingLeft: '40px', marginTop: '20px' }}>
                    {/* Timeline line */}
                    <div style={{
                      position: 'absolute',
                      left: '18px',
                      top: '10px',
                      bottom: '10px',
                      width: '3px',
                      background: 'linear-gradient(180deg, #8b5cf6 0%, #d8b4fe 100%)',
                      borderRadius: '0px'
                    }} />

                    {activities.map((activity, index) => {
                      const getActivityIcon = (type) => {
                        const iconMap = {
                          'comment': '💬',
                          'called': '📞',
                          'emailed': '📧',
                          'email_reply': '📩',
                          'quotation_sent': '📄',
                          'meeting_scheduled': '📅',
                          'follow_up': '🔔',
                          'issue': '⚠️',
                          'resolved': '✅',
                          'created': '🎯',
                          'status_changed': '🔄',
                          'assigned': '👤',
                          'note_added': '📝'
                        };
                        return iconMap[type] || '📌';
                      };

                      const getActivityColor = (type) => {
                        const colorMap = {
                          'comment': '#8b5cf6',
                          'called': '#3b82f6',
                          'emailed': '#10b981',
                          'email_reply': '#0ea5e9',
                          'quotation_sent': '#f59e0b',
                          'meeting_scheduled': '#ec4899',
                          'follow_up': '#06b6d4',
                          'issue': '#ef4444',
                          'resolved': '#22c55e',
                          'created': '#6366f1',
                          'status_changed': '#a855f7',
                          'assigned': '#8b5cf6',
                          'note_added': '#64748b'
                        };
                        return colorMap[type] || '#64748b';
                      };

                      return (
                        <div
                          key={activity.id}
                          className="timeline-item"
                          style={{
                            position: 'relative',
                            marginBottom: index === activities.length - 1 ? '0' : '24px',
                            paddingLeft: '20px'
                          }}
                        >
                          {/* Timeline marker */}
                          <div style={{
                            position: 'absolute',
                            left: '-26px',
                            top: '6px',
                            width: '36px',
                            height: '36px',
                            borderRadius: '0px',
                            backgroundColor: 'white',
                            border: `3px solid ${getActivityColor(activity.activity_type)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            zIndex: 1
                          }}>
                            {getActivityIcon(activity.activity_type)}
                          </div>

                          {/* Content card */}
                          <div style={{
                            padding: '14px 16px',
                            background: 'white',
                            borderRadius: '0px',
                            border: `2px solid ${getActivityColor(activity.activity_type)}20`,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            transition: 'all 0.2s'
                          }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.04)';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}>
                              <span style={{
                                fontWeight: '700',
                                fontSize: '13px',
                                color: getActivityColor(activity.activity_type),
                                letterSpacing: '0.3px'
                              }}>
                                {formatActivityLabel(activity)}
                              </span>
                              <span style={{
                                fontSize: '11px',
                                color: '#94a3b8',
                                fontWeight: '500'
                              }}>
                                {new Date(activity.activity_date).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            {activity.notes && (
                              <div style={{
                                fontSize: '13px',
                                color: '#475569',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                background: activity.activity_type === 'email_reply' ? '#f8fafc' : 'transparent',
                                padding: activity.activity_type === 'email_reply' ? '12px' : '0',
                                borderRadius: '0px',
                                marginTop: '8px',
                                border: activity.activity_type === 'email_reply' ? '1px solid #e2e8f0' : 'none'
                              }}>
                                {activity.activity_type === 'email_reply' && activity.activity_data?.full_text
                                  ? (
                                    <div className="email-content-wrapper">
                                      <div style={{ fontWeight: '600', marginBottom: '6px', color: '#64748b', fontSize: '11px', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>MESSAGE CONTENT:</span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDebugPayload(activity.activity_data);
                                            setShowRawDataModal(true);
                                          }}
                                          style={{ border: 'none', background: 'none', color: '#8b5cf6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}
                                          title="View Raw Webhook Data"
                                        >
                                          <FileJson size={12} /> View Raw Data
                                        </button>
                                      </div>
                                      {activity.activity_data.full_text}
                                    </div>
                                  )
                                  : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {activity.notes}
                                      {activity.activity_type === 'email_reply' && !activity.activity_data?.full_text && (
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          color: '#ef4444',
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          background: '#fef2f2',
                                          padding: '4px 8px',
                                          borderRadius: '0px'
                                        }}>
                                          <AlertTriangle size={12} /> Content extraction failed.
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setDebugPayload(activity.activity_data);
                                              setShowRawDataModal(true);
                                            }}
                                            style={{ border: 'none', background: 'none', color: '#ef4444', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                                          >
                                            Debug Raw Data
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )
                                }
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Quotation History Section (Temporarily Hidden for Whitelabel) */}
              {false && (
                <div className="quotation-history" style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}>
                    <FileText size={18} />
                    Quotation History
                  </h4>
                  {loadingQuotations ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                      Loading quotations...
                    </div>
                  ) : leadQuotations.length === 0 ? (
                    <p className="no-activities" style={{ padding: '20px', textAlign: 'center', color: '#6c757d', background: '#f8f9fa', borderRadius: '0px' }}>
                      No quotations sent yet
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {leadQuotations.map((quotation) => (
                        <div
                          key={quotation.id}
                          style={{
                            padding: '15px',
                            background: '#fff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '0px',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            e.currentTarget.style.borderColor = '#4f46e5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = '#e0e0e0';
                          }}
                          onClick={() => window.open(`/quotations?id=${quotation.id}`, '_blank')}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937', marginBottom: '4px' }}>
                                {quotation.quotation_number}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                Created: {new Date(quotation.created_at).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                            <div>
                              {getQuotationStatusBadge(quotation.status)}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                            <div>
                              <span style={{ color: '#6c757d' }}>Amount: </span>
                              <span style={{ fontWeight: '600', color: '#059669' }}>
                                ₹{parseFloat(quotation.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div>
                              <span style={{ color: '#6c757d' }}>Items: </span>
                              <span style={{ fontWeight: '600' }}>{quotation.items_count || 0}</span>
                            </div>
                            <div>
                              <span style={{ color: '#6c757d' }}>Created By: </span>
                              <span style={{ fontWeight: '600', color: '#1e40af' }}>
                                {quotation.created_by_role_name || quotation.created_by_username || 'Main Client'}
                              </span>
                            </div>
                            {quotation.valid_until && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#6c757d' }}>Valid Until: </span>
                                <span style={{ fontWeight: '500' }}>
                                  {new Date(quotation.valid_until).toLocaleDateString('en-IN', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid #e5e7eb',
                              flexWrap: 'wrap'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewQuotation(quotation);
                              }}
                              className="quotation-action-btn quotation-action-view"
                              title="View Details"
                              style={{
                                padding: '6px 16px',
                                fontSize: '12px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '0px',
                                background: 'white',
                                color: '#475569',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                minWidth: '85px',
                                justifyContent: 'center',
                                flex: '1 1 auto'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.borderColor = '#94a3b8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = '#cbd5e1';
                              }}
                            >
                              <Eye size={14} />
                              View
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditQuotation(quotation);
                              }}
                              className="quotation-action-btn quotation-action-edit"
                              title="Edit Quotation"
                              style={{
                                padding: '6px 16px',
                                fontSize: '12px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '0px',
                                background: 'white',
                                color: '#475569',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                minWidth: '85px',
                                justifyContent: 'center',
                                flex: '1 1 auto'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.borderColor = '#94a3b8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = '#cbd5e1';
                              }}
                            >
                              <Edit size={14} />
                              Edit
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteQuotation(quotation.id, quotation.quotation_number);
                              }}
                              className="quotation-action-btn quotation-action-delete"
                              title="Delete Quotation"
                              style={{
                                padding: '6px 16px',
                                fontSize: '12px',
                                border: '1px solid #fecdd3',
                                borderRadius: '0px',
                                background: 'white',
                                color: '#e11d48',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                minWidth: '85px',
                                justifyContent: 'center',
                                flex: '1 1 auto'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#fef2f2';
                                e.currentTarget.style.borderColor = '#fda4af';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = '#fecdd3';
                              }}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>

                            {quotation.status !== 'sent' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendQuotationWhatsApp(quotation);
                                }}
                                disabled={sendingWhatsappId === quotation.id}
                                className="quotation-action-btn quotation-action-send"
                                title="Send via WhatsApp with Accept/Reject buttons"
                                style={{
                                  padding: '6px 16px',
                                  fontSize: '12px',
                                  border: '1px solid #bfdbfe',
                                  borderRadius: '0px',
                                  background: sendingWhatsappId === quotation.id ? '#eff6ff' : 'white',
                                  color: '#2563eb',
                                  cursor: sendingWhatsappId === quotation.id ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s',
                                  opacity: sendingWhatsappId === quotation.id ? 0.7 : 1,
                                  minWidth: '85px',
                                  justifyContent: 'center',
                                  flex: '1 1 auto'
                                }}
                                onMouseEnter={(e) => {
                                  if (sendingWhatsappId !== quotation.id) {
                                    e.currentTarget.style.background = '#eff6ff';
                                    e.currentTarget.style.borderColor = '#93c5fd';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'white';
                                  e.currentTarget.style.borderColor = '#bfdbfe';
                                }}
                              >
                                <Send size={14} />
                                {sendingWhatsappId === quotation.id ? 'Sending...' : '📱 Send'}
                              </button>
                            )}

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendQuotationEmail(quotation);
                              }}
                              disabled={sendingEmailId === quotation.id}
                              className="quotation-action-btn quotation-action-email"
                              title="Send Email with Accept/Reject Buttons"
                              style={{
                                padding: '6px 16px',
                                fontSize: '12px',
                                border: '1px solid #c4b5fd',
                                borderRadius: '0px',
                                background: 'white',
                                color: '#7c3aed',
                                cursor: sendingEmailId === quotation.id ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                opacity: sendingEmailId === quotation.id ? 0.6 : 1,
                                minWidth: '85px',
                                justifyContent: 'center',
                                flex: '1 1 auto'
                              }}
                              onMouseEnter={(e) => {
                                if (sendingEmailId !== quotation.id) {
                                  e.currentTarget.style.background = '#faf5ff';
                                  e.currentTarget.style.borderColor = '#a78bfa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = '#c4b5fd';
                              }}
                            >
                              <Send size={14} />
                              {sendingEmailId === quotation.id ? 'Sending...' : 'Email'}
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBillDownload(quotation.id, quotation.quotation_number);
                              }}
                              disabled={downloadingId === quotation.id}
                              className="quotation-action-btn quotation-action-download"
                              title="Download PDF"
                              style={{
                                padding: '6px 16px',
                                fontSize: '12px',
                                border: '1px solid #d1fae5',
                                borderRadius: '0px',
                                background: downloadedIds.has(quotation.id) ? '#d1fae5' : 'white',
                                color: '#059669',
                                cursor: downloadingId === quotation.id ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                opacity: downloadingId === quotation.id ? 0.6 : 1,
                                minWidth: '85px',
                                justifyContent: 'center',
                                flex: '1 1 auto'
                              }}
                              onMouseEnter={(e) => {
                                if (downloadingId !== quotation.id) {
                                  e.currentTarget.style.background = '#d1fae5';
                                  e.currentTarget.style.borderColor = '#6ee7b7';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = downloadedIds.has(quotation.id) ? '#d1fae5' : 'white';
                                e.currentTarget.style.borderColor = '#d1fae5';
                              }}
                            >
                              <FileText size={14} />
                              {downloadingId === quotation.id ? 'Downloading...' : downloadedIds.has(quotation.id) ? 'Downloaded ✓' : 'PDF'}
                            </button>

                            {quotation.status === 'sent' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConvertToOrder(quotation.id, quotation.quotation_number);
                                }}
                                className="quotation-action-btn quotation-action-convert"
                                title="Convert to Order"
                                style={{
                                  padding: '6px 16px',
                                  fontSize: '12px',
                                  border: '1px solid #fde68a',
                                  borderRadius: '0px',
                                  background: 'white',
                                  color: '#d97706',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s',
                                  minWidth: '120px',
                                  justifyContent: 'center',
                                  flex: '1 1 auto'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#fef3c7';
                                  e.currentTarget.style.borderColor = '#fcd34d';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'white';
                                  e.currentTarget.style.borderColor = '#fde68a';
                                }}
                              >
                                <ArrowRight size={14} />
                                Convert to Order
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Modal Actions */}
              <div className="lead-modal-actions">
                {!readonly && scope !== 'hierarchy' && viewMode !== 'hierarchy-readonly' && (
                  <>
                    {/* Call Buttons Row */}
                    <div className="lead-modal-call-buttons-row">
                      <button
                        className="btn lead-btn-manual-call"
                        onClick={() => { const raw = selectedLead.mobile_number || ''; const normalized = raw.startsWith('+620') ? '+62' + raw.slice(4) : raw.startsWith('08') ? '+62' + raw.slice(1) : raw; window.dispatchEvent(new CustomEvent('INITIATE_PLIVO_CALL', { detail: { phoneNumber: normalized, clientId: user?.id, skipDialpad: true } })); }}
                      >
                        <Phone size={16} /> Manual Call
                      </button>

                      <button
                        className="btn lead-btn-wp-call"
                        onClick={() => window.dispatchEvent(new CustomEvent('INITIATE_WA_CALL', { detail: { phoneNumber: selectedLead.mobile_number, clientId: user?.id } }))}
                      >
                        <MessageSquare size={16} style={{ visibility: 'hidden' }} /> WP Call
                      </button>

                      <button
                        className="btn lead-btn-wp-chat"
                        onClick={() => {
                          setNewChatPhone(selectedLead.mobile_number || '');
                          setNewChatPhoneEdited(false);
                          setShowNewChatModal(true);
                        }}
                      >
                        <MessageSquare size={16} style={{ visibility: 'hidden' }} /> WP Chat
                      </button>
                    </div>

                    {/* Convert Button - if qualified */}
                    {selectedLead.lead_status === 'qualified' && (
                      <button
                        className="btn lead-btn-convert"
                        onClick={() => convertLead(selectedLead.id)}
                      >
                        <UserPlus size={16} />
                        Convert to Customer
                      </button>
                    )}
                  </>
                )}

                {/* Close Button */}
                <button
                  className="btn lead-btn-close"
                  onClick={() => setShowLeadModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Assignment Modal */}
      {showAssignmentModal && (
        <LeadAssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => setShowAssignmentModal(false)}
          onAssignSuccess={() => {
            fetchLeads();
            fetchStatistics();
          }}
          totalUnassignedLeads={statistics?.unassigned_leads || 0}
        />
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <AddLeadModal
          isOpen={showAddLeadModal}
          onClose={() => setShowAddLeadModal(false)}
          onLeadAdded={() => {
            fetchLeads();
            fetchStatistics();
          }}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onUploadSuccess={() => {
            fetchLeads();
            fetchStatistics();
          }}
        />
      )}

      {/* Quotation Modal */}
      {showQuotationModal && (
        <QuotationForm
          isOpen={showQuotationModal}
          onClose={() => setShowQuotationModal(false)}
          onSuccess={(quotation) => {
            console.log('✅ Quotation created:', quotation);
            setShowQuotationModal(false);
            // Refresh lead quotations after creating a new one
            if (selectedLead?.id) {
              fetchLeadQuotations(selectedLead.id);
            }
            fetchLeads();
          }}
          products={products}
          services={services}
          leadId={selectedLead?.id}
          leadData={selectedLead}
        />
      )}

      {/* Quotation View Modal */}
      {showViewModal && viewingQuotation && (
        <QuotationViewModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setViewingQuotation(null);
          }}
          quotation={viewingQuotation}
        />
      )}

      {/* Quotation Edit Modal */}
      {showEditModal && editingQuotation && (
        <QuotationForm
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingQuotation(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingQuotation(null);
            // Refresh lead quotations after editing
            if (selectedLead?.id) {
              fetchLeadQuotations(selectedLead.id);
            }
            fetchLeads();
          }}
          editQuotation={editingQuotation}
          products={products}
          services={services}
        />
      )}
      {/* Raw Data Debug Modal */}
      {showRawDataModal && debugPayload && (
        <div className="modal-overlay" onClick={() => setShowRawDataModal(false)} style={{ zIndex: 3000 }}>
          <div className="modal-content debug-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header" style={{ background: '#1e293b', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileJson size={20} />
                <h3>Raw Webhook Data Inspection</h3>
              </div>
              <button onClick={() => setShowRawDataModal(false)} style={{ color: 'white' }}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '0' }}>
              <div style={{
                background: '#0f172a',
                color: '#cbd5e1',
                padding: '20px',
                fontFamily: 'monospace',
                fontSize: '13px',
                overflow: 'auto',
                maxHeight: '60vh'
              }}>
                <pre>{JSON.stringify(debugPayload, null, 2)}</pre>
              </div>
              <div style={{ padding: '20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px', color: '#475569', fontSize: '13px' }}>
                  <AlertTriangle size={18} style={{ color: '#f59e0b', flexShrink: 0 }} />
                  <div>
                    <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#1e293b' }}>Why is this here?</h4>
                    <p style={{ margin: 0 }}>This payload was captured from Resend. If <code>full_text</code> is empty but you see content in <code>raw_payload</code>, it means our extraction logic needs adjustment for this specific email format.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowRawDataModal(false)}>Close Inspector</button>
            </div>
          </div>
        </div>
      )}

      {/* New WhatsApp Chat Starter Modal */}
      {showNewChatModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowNewChatModal(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '0px',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              backgroundColor: '#f8fafc'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '32px', height: '32px', backgroundColor: '#dcfce7', color: '#16a34a', borderRadius: '0px'
                }}>
                  <MessageSquare size={16} />
                </span>
                New Chat
              </h2>
              <button
                onClick={() => setShowNewChatModal(false)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}
              >
                &times;
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                WhatsApp Number
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                {/* Country Code Selector */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '0px',
                      backgroundColor: 'white', cursor: 'pointer', fontSize: '14px', height: '42px', minWidth: '90px'
                    }}
                  >
                    <span>{selectedCountry.flag}</span>
                    <span>{selectedCountry.code}</span>
                    <ChevronDown size={14} style={{ color: '#94a3b8' }} />
                  </button>

                  {showCountryDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                      width: '240px', backgroundColor: 'white', border: '1px solid #e2e8f0',
                      borderRadius: '0px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                      zIndex: 10001, maxHeight: '250px', display: 'flex', flexDirection: 'column'
                    }}>
                      <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9' }}>
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          style={{
                            width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0',
                            borderRadius: '0px', fontSize: '13px', outline: 'none'
                          }}
                        />
                      </div>
                      <div style={{ overflowY: 'auto', flex: 1 }}>
                        {COUNTRY_CODES.filter(c =>
                          c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
                          c.code.includes(countrySearch)
                        ).map(country => (
                          <div
                            key={`${country.iso}-${country.code}`}
                            onClick={() => {
                              setSelectedCountry(country);
                              setShowCountryDropdown(false);
                              setCountrySearch('');
                            }}
                            style={{
                              padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                              cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f8fafc'
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <span style={{ fontSize: '16px' }}>{country.flag}</span>
                            <span style={{ flex: 1 }}>{country.country}</span>
                            <span style={{ color: '#64748b', fontWeight: '500' }}>{country.code}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Phone Number Input */}
                <input
                  type="text"
                  placeholder="Enter number..."
                  value={newChatPhone}
                  onChange={(e) => {
                    setNewChatPhone(e.target.value);
                    setNewChatPhoneEdited(true);
                  }}
                  style={{
                    flex: 1,
                    minWidth: '180px',
                    maxWidth: '240px',
                    height: '42px',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0px',
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'text',
                    letterSpacing: 'normal',
                    backgroundColor: 'white'
                  }}
                  onFocus={() => { }}
                  onBlur={() => { }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newChatPhone) sendNewChatTemplate();
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                  Starter Template
                </label>
                <select
                  value={selectedTemplateForChat}
                  onChange={(e) => setSelectedTemplateForChat(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0',
                    borderRadius: '0px', fontSize: '14px', outline: 'none', backgroundColor: 'white'
                  }}
                >
                  <option value="tecnvi_he">tecnvi_he (Default Starter)</option>
                  {whatsappTemplates.filter(t => t.name !== 'tecnvi_he').map(t => (
                    <option key={t.id || t.name} value={t.name}>{t.name}</option>
                  ))}
                </select>
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#94a3b8' }}>
                  A WhatsApp template must be sent to start a 24-hour conversation window if one isn't currently open.
                </p>
              </div>

              {/* Toast / Status Message */}
              {newChatToast && (
                <div style={{
                  padding: '10px 14px', borderRadius: '0px', marginBottom: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
                  backgroundColor: newChatToast.type === 'success' ? '#f0fdf4' : '#fef2f2',
                  color: newChatToast.type === 'success' ? '#16a34a' : '#ef4444',
                  border: `1px solid ${newChatToast.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                }}>
                  {newChatToast.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                  {newChatToast.msg}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  onClick={() => setShowNewChatModal(false)}
                  style={{
                    padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '0px',
                    backgroundColor: 'white', color: '#475569', fontSize: '14px', fontWeight: '500',
                    cursor: 'pointer', transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => e.target.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                >
                  Cancel
                </button>
                <button
                  onClick={sendNewChatTemplate}
                  disabled={!newChatPhone || newChatSending}
                  style={{
                    padding: '10px 20px', border: 'none', borderRadius: '0px',
                    backgroundColor: (!newChatPhone || newChatSending) ? '#94a3b8' : '#16a34a',
                    color: 'white', fontSize: '14px', fontWeight: '500',
                    cursor: (!newChatPhone || newChatSending) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s'
                  }}
                >
                  <Send size={16} />
                  {newChatSending ? 'Sending...' : 'Send Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement;
